import { Cell } from './Cell';
import { HeaderModel, TableViewRow } from './types';
import { colors } from '@ardoq/design-tokens';
import styled, { css } from 'styled-components';
import { Text } from '@ardoq/typography';
import { Box } from '@ardoq/layout';
import { MarkdownViewer } from '@ardoq/markdown';
import { t, type I18nLocale } from '@ardoq/i18n';
import { type Locale } from '@ardoq/locale';

const RowItem = styled.tr<{
  $isHighlighted?: boolean;
  $isDescriptionExpanded?: boolean;
  $isDescription?: boolean;
}>`
  position: relative;

  ${({ $isHighlighted, $isDescriptionExpanded, $isDescription }) =>
    $isHighlighted &&
    css`
      background-color: ${colors.blue400}10 !important;
      &:after {
        position: absolute;
        z-index: 1;
        pointer-events: none;
        content: ' ';
        inset: 0;
        border: 2px solid ${colors.blue400};
        ${
          $isDescriptionExpanded
            ? $isDescription
              ? 'border-top: 0;'
              : 'border-bottom: 0;'
            : ''
        }
      }
    `}
`;

type ViewProps = {
  model: TableViewRow;
  headers: HeaderModel[];
  isHighlighted?: boolean;
  rowIndex: number;
  locale: Locale;
  i18nLocale: I18nLocale;
};

const Row = ({
  model,
  headers,
  isHighlighted,
  rowIndex,
  locale,
  i18nLocale,
}: ViewProps) => (
  <>
    <RowItem
      id={model.cid}
      data-id={model.id}
      data-parent={model.parent && model.parent.id}
      $isHighlighted={isHighlighted}
      $isDescriptionExpanded={model.expandDescription}
    >
      {headers.map((header, i) => (
        <Cell
          rowIndex={rowIndex}
          key={`${i}-${header.key}`}
          type={header.cellType}
          attribute={header.key}
          isExpandController={header.isExpandController}
          model={model}
          numberFormatOptions={header.numberFormatOptions}
          locale={locale}
          i18nLocale={i18nLocale}
        />
      ))}
    </RowItem>
    {model.expandDescription && (
      <RowItem
        $isHighlighted={isHighlighted}
        $isDescriptionExpanded
        $isDescription
      >
        <td colSpan={headers.length}>
          <Box paddingTop="small">
            <Text variant="text2Bold">
              {t(
                i18nLocale,
                'tabview:tableViews.descriptionLabel',
                'Description:'
              )}
            </Text>
            <MarkdownViewer
              content={model.markdownDescription}
              sanitationLevel="permissive"
            />
          </Box>
        </td>
      </RowItem>
    )}
  </>
);

export { Row };
